import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import styled from "styled-components";
import clienteAxios from "../../config/Axios";
import { Helmet } from "react-helmet-async";
import {
    Box,
    Typography,
    Container,
    Button,
    CircularProgress,
    Fade,
} from "@mui/material";
import { Header1 } from "../../components/Layout/Header1";
import { Footer } from "../../components/Layout/Footer";
import { PlayArrowRounded, OpenInNewRounded } from "@mui/icons-material";

const VideoContainer = styled.div`
  width: 100%;
  max-width: 400px;
  aspect-ratio: 9/16;
  background-color: #111;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 16px 40px rgba(128, 31, 86, 0.2);
  margin: 0 auto;
  cursor: pointer;
`;

const VideoElement = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PlayIconOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 70px;
  height: 70px;
  background: rgba(128, 31, 86, 0.7);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  backdrop-filter: blur(4px);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(128, 31, 86, 0.9);
    transform: translate(-50%, -50%) scale(1.1);
  }
`;

const Avatar = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #801F56;
`;

const ReelShare = () => {
    const { id } = useParams();
    const [reel, setReel] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [appOpened, setAppOpened] = useState(false);

    useEffect(() => {
        const fetchReel = async () => {
            try {
                const response = await clienteAxios.get(`/reels/public/${id}`);
                setReel(response.data);
            } catch (err) {
                console.error("Error fetching reel:", err);
                setError("Reel no encontrado o no disponible.");
            } finally {
                setLoading(false);
            }
        };

        fetchReel();
    }, [id]);

    useEffect(() => {
        // Attempt to redirect automatically only ONCE to prevent infinite loops on iOS
        // If the user lands here, we try to open the app. If it fails, they stay on the page.
        // We use a flag in sessionStorage to know if we already tried redirecting in this session.
        if (!loading && reel && !appOpened) {
            const hasTriedRedirect = sessionStorage.getItem(`redirected_reel_${id}`);
            if (!hasTriedRedirect) {
                sessionStorage.setItem(`redirected_reel_${id}`, 'true');
                setAppOpened(true);
                const timer = setTimeout(() => {
                    handleOpenApp(null, true);
                }, 1000);
                return () => clearTimeout(timer);
            }
        }
    }, [loading, reel, id, appOpened]);

    const getMobileOperatingSystem = () => {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        if (/android/i.test(userAgent)) return "Android";
        if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) return "iOS";
        return "unknown";
    };

    const handleOpenApp = (e, isAuto = false) => {
        if (e) e.preventDefault();

        const os = getMobileOperatingSystem();
        const currentUrl = window.location.href;
        let appUrl = currentUrl;

        if (os === "Android") {
            const packageId = "com.MerryColor.DistribucionMerryColor";
            appUrl = `intent://merrycolor.mx/reel/${id}#Intent;scheme=https;package=${packageId};end;`;
            window.location.replace(appUrl);
        } else if (os === "iOS") {
            // For iOS, if it's an auto-redirect, doing window.location.replace to the exact same URL 
            // inside the browser will cause an infinite loop if Universal Links fail.
            // If the user clicks explicitly, we can try to force it, or point to a custom scheme if available.
            // A common workaround is pointing to the App Store if Universal link is broken,
            // or just letting the user view it on the web.
            if (!isAuto) {
                // If manual click, we can try to navigate
                window.location.href = `https://merrycolor.mx/reel/${id}`;
            }
        } else {
            if (!isAuto) {
                alert("La aplicación nativa solo está disponible en iOS y Android.");
            }
        }
    };

    const togglePlay = (e) => {
        const video = document.getElementById("reel-video-element");
        if (video) {
            if (video.paused) {
                video.play();
                setIsPlaying(true);
            } else {
                video.pause();
                setIsPlaying(false);
            }
        }
    };

    return (
        <Box sx={{ backgroundColor: "#fcfcfc", minHeight: "100vh", display: 'flex', flexDirection: 'column' }}>
            <Header1 />

            {loading ? (
                <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <CircularProgress sx={{ color: "#801F56" }} />
                </Box>
            ) : error || !reel ? (
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", p: 3, textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ color: "#801F56", fontWeight: "bold", mb: 2 }}>{error || "Reel no encontrado"}</Typography>
                    <Typography sx={{ color: "#666", mb: 4 }}>El reel que buscas no existe o ha sido eliminado.</Typography>
                    <Button variant="contained" component={Link} to="/catalogo" sx={{ backgroundColor: "#801F56", '&:hover': { backgroundColor: "#a83279" }, borderRadius: '25px', px: 4, py: 1.5 }}>
                        Ver Catálogo
                    </Button>
                </Box>
            ) : (
                <Fade in={true}>
                    <Box sx={{ flex: 1, py: { xs: 4, md: 8 } }}>
                        <Helmet>
                            <title>Reel de {reel.client?.name || "MerryColor"}</title>
                            <meta name="description" content={reel.description || "Mira este increíble Reel en MerryColor"} />
                            <meta name="apple-itunes-app" content={`app-id=123456789, app-argument=https://merrycolor.mx/reel/${id}`} />
                            <meta property="og:title" content={`Reel de ${reel.client?.name || "MerryColor"}`} />
                            <meta property="og:description" content={reel.description || "Mira este increíble Reel en MerryColor"} />
                            <meta property="og:video" content={reel.video_url} />
                            <meta property="og:type" content="video.other" />
                            <meta property="al:android:package" content="com.MerryColor.DistribucionMerryColor" />
                            <meta property="al:android:url" content={`https://merrycolor.mx/reel/${id}`} />
                            <meta property="al:ios:url" content={`https://merrycolor.mx/reel/${id}`} />
                            <meta property="al:ios:app_store_id" content="4N62C39G46" />
                        </Helmet>

                        <Container maxWidth="md">
                            <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: { xs: 4, md: 8 }, alignItems: { xs: "center", md: "flex-start" }, justifyContent: "center" }}>

                                {/* Video Column */}
                                <Box sx={{ width: "100%", maxWidth: "400px" }}>
                                    <VideoContainer onClick={togglePlay}>
                                        <VideoElement
                                            id="reel-video-element"
                                            src={reel.video_url}
                                            loop
                                            muted={false}
                                            playsInline
                                            poster={reel.thumbnail || "/img/cover-video-placeholder.png"}
                                        />
                                        {!isPlaying && (
                                            <PlayIconOverlay>
                                                <PlayArrowRounded sx={{ fontSize: 40 }} />
                                            </PlayIconOverlay>
                                        )}
                                    </VideoContainer>
                                </Box>

                                {/* Info Column */}
                                <Box sx={{ width: "100%", maxWidth: "400px", display: "flex", flexDirection: "column", gap: 3 }}>

                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, backgroundColor: "#fff", borderRadius: "16px", boxShadow: "0 4px 12px rgba(0,0,0,0.03)" }}>
                                        <Avatar src={reel.client?.profile_image || "/img/default-avatar.png"} alt={reel.client?.name || "Usuario"} />
                                        <Box>
                                            <Typography sx={{ fontWeight: 700, color: "#111", fontSize: "1.1rem" }}>
                                                {reel.client?.name || "Usuario de MerryColor"} {reel.client?.first_lastname || ""}
                                            </Typography>
                                            <Typography sx={{ color: "#888", fontSize: "0.9rem" }}>Creador</Typography>
                                        </Box>
                                    </Box>

                                    {reel.description && (
                                        <Box sx={{ p: 2, backgroundColor: "#fff", borderRadius: "16px", boxShadow: "0 4px 12px rgba(0,0,0,0.03)" }}>
                                            <Typography sx={{ color: "#444", fontSize: "1rem", lineHeight: 1.6 }}>
                                                {reel.description}
                                            </Typography>
                                        </Box>
                                    )}

                                    <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                                        <Button
                                            onClick={(e) => handleOpenApp(e, false)}
                                            variant="contained"
                                            fullWidth
                                            startIcon={<OpenInNewRounded />}
                                            sx={{
                                                backgroundColor: "#801F56",
                                                color: "white",
                                                fontWeight: "bold",
                                                fontSize: "1.1rem",
                                                padding: "14px 24px",
                                                borderRadius: "25px",
                                                textTransform: "none",
                                                boxShadow: "0 10px 20px rgba(128, 31, 86, 0.25)",
                                                '&:hover': {
                                                    backgroundColor: "#a83279",
                                                    boxShadow: "0 10px 25px rgba(128, 31, 86, 0.35)",
                                                }
                                            }}
                                        >
                                            Abrir en la App
                                        </Button>

                                        <Typography sx={{ fontSize: '0.8rem', color: '#888', textAlign: 'center' }}>
                                            * Si tienes la app instalada, se abrirá automáticamente.
                                        </Typography>
                                    </Box>

                                </Box>
                            </Box>
                        </Container>
                    </Box>
                </Fade>
            )}

            <Footer />
        </Box>
    );
};

export default ReelShare;
